import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { authAPI } from "../services/api";
import { toast } from "react-toastify";
import {
  User,
  Key,
  Save,
  Copy,
  RefreshCw,
  Shield,
  AlertCircle,
} from "lucide-react";
import { safeFormatDateOnly, safeFormatDateTime } from "../utils/dateUtils";

function Profile() {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [publicKey, setPublicKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      setProfile(response.data);
      setPublicKey(response.data.publicKey || "");
    } catch (error) {
      toast.error("Failed to fetch profile");
      console.error("Profile error:", error);
    } finally {
      setLoading(false);
    }
  };

  const generatePublicKey = () => {
    // Simple public key generation (in real app, this would be cryptographically secure)
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 15);
    const key = `PUB_${timestamp}_${randomStr}`;
    setPublicKey(key);
  };

  const copyPublicKey = () => {
    if (publicKey) {
      navigator.clipboard.writeText(publicKey);
      toast.success("Public key copied to clipboard");
    }
  };

  const handleSavePublicKey = async () => {
    setSaving(true);
    try {
      await authAPI.updatePublicKey(publicKey);
      updateUser({ publicKey });
      toast.success("Public key updated successfully");
      fetchProfile(); // Refresh profile data
    } catch (error) {
      toast.error("Failed to update public key");
      console.error("Update public key error:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
          <p className="mt-2 text-gray-600">
            Manage your account information and security settings
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Account Information */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">
                Account Information
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    User ID
                  </label>
                  <div className="mt-1 flex items-center space-x-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {user?.userId}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Account Created
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {profile?.createdAt
                      ? safeFormatDateOnly(profile.createdAt)
                      : "Unknown"}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Last Login
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {profile?.lastLogin
                      ? safeFormatDateTime(profile.lastLogin)
                      : "Never"}
                  </p>
                </div>
              </div>
            </div>

            {/* Public Key Management */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">
                Public Key Management
              </h2>

              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-700">
                      About Public Keys
                    </p>
                    <p className="text-sm text-blue-600 mt-1">
                      Your public key is used to verify your digital signatures.
                      It can be shared with others to allow them to verify
                      documents you've signed.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Public Key
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={publicKey}
                      onChange={(e) => setPublicKey(e.target.value)}
                      className="flex-1 input-field font-mono text-sm"
                      placeholder="Generate or enter your public key"
                    />
                    <button
                      onClick={copyPublicKey}
                      disabled={!publicKey}
                      className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      title="Copy public key"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      onClick={generatePublicKey}
                      className="btn-secondary flex items-center space-x-1 whitespace-nowrap"
                    >
                      <RefreshCw className="h-4 w-4" />
                      <span>Generate</span>
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {publicKey
                      ? 'Public key is ready. Click "Save" to update your profile.'
                      : 'Click "Generate" to create a new public key.'}
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleSavePublicKey}
                    disabled={
                      !publicKey || saving || publicKey === profile?.publicKey
                    }
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    <span>{saving ? "Saving..." : "Save Public Key"}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Security Status */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Security Status</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Shield
                    className={`h-5 w-5 ${
                      profile?.publicKey ? "text-green-500" : "text-yellow-500"
                    }`}
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Public Key
                    </p>
                    <p
                      className={`text-sm ${
                        profile?.publicKey
                          ? "text-green-600"
                          : "text-yellow-600"
                      }`}
                    >
                      {profile?.publicKey ? "Configured" : "Not Set"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Key className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Account Security
                    </p>
                    <p className="text-sm text-green-600">Active</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Public Key Info */}
            {profile?.publicKey && (
              <div className="card p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Current Public Key
                </h3>
                <div className="space-y-2">
                  <p className="text-sm text-gray-500">Active public key:</p>
                  <code className="block p-3 bg-gray-100 rounded-md text-xs font-mono break-all">
                    {profile.publicKey}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(profile.publicKey);
                      toast.success("Current public key copied");
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Copy current key
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
